import { EngineContext, EngineScene } from '../../engine';

export interface Scene extends EngineScene {
  key: string;
  onEnter?(context: EngineContext, payload?: unknown): void;
  onExit?(): void;
}

interface PendingTransition {
  key: string;
  payload?: unknown;
}

export class SceneManager implements EngineScene {
  private scenes = new Map<string, Scene>();
  private currentScene?: Scene;
  private pending?: PendingTransition;
  private initial?: PendingTransition;

  init(context: EngineContext): void {
    if (this.initial) {
      this.start(this.initial.key, context, this.initial.payload);
      this.initial = undefined;
    }
  }

  register(scene: Scene): void {
    this.scenes.set(scene.key, scene);
  }

  start(key: string, context?: EngineContext, payload?: unknown): void {
    if (!context) {
      this.initial = { key, payload };
      return;
    }
    const next = this.scenes.get(key);
    if (!next) {
      throw new Error(`Scene ${key} not registered`);
    }
    this.currentScene = next;
    next.init?.(context);
    next.onEnter?.(context, payload);
  }

  transition(key: string, payload?: unknown): void {
    this.pending = { key, payload };
  }

  update(context: EngineContext): void {
    if (!this.currentScene && this.initial) {
      const next = this.scenes.get(this.initial.key);
      if (!next) {
        throw new Error(`Scene ${this.initial.key} not registered`);
      }
      this.currentScene = next;
      next.init?.(context);
      next.onEnter?.(context, this.initial.payload);
      this.initial = undefined;
    }

    if (this.pending) {
      const next = this.scenes.get(this.pending.key);
      if (!next) {
        throw new Error(`Scene ${this.pending.key} not registered`);
      }
      this.currentScene?.onExit?.();
      this.currentScene = next;
      next.init?.(context);
      next.onEnter?.(context, this.pending.payload);
      this.pending = undefined;
    }
    this.currentScene?.update(context);
  }

  render(context: EngineContext): void {
    this.currentScene?.render(context);
  }
}
